<script setup>
import { translate } from '@/plugins/i18n.js'
import ApiController from '@/services/ApiController'
import MessageHelper from '@/services/helpers/MessageHelper'
import { ref } from 'vue'

const { orderHash, email } = defineProps({
  orderHash: String,
  email: String
})

const pwRef = ref(null)
const password = ref(null)
const showPassword = ref(false)
const isPending = ref(false)
const hasSetPassword = ref(false)

const rules = {
  required: (value) => !!value || translate('validation_msg_required'),
  minLength: (value) =>
    (value?.length ?? 0) >= 8 || translate('validation_msg_min', 8)
}

const setPassword = async () => {
  isPending.value = true
  try {
    await ApiController.setPassword({
      orderHash,
      email,
      password: password.value
    })
    hasSetPassword.value = true
  } catch (error) {
    MessageHelper.addErrorMessage(
      translate('checkout:password_set_error'),
      null,
      5000,
      error,
      'checkout_register_password_set_failed'
    )
  } finally {
    isPending.value = false
  }
}
</script>

<template>
  <div
    class="success-register-customer d-flex text-md-start flex-column px-3 w-100"
  >
    <div
      v-if="!hasSetPassword"
      class="add-pw font-weight-bold py-5 px-4 text-center"
    >
      {{ translate('checkout:add_pw') }}
    </div>

    <VSheet
      color="light-beige"
      elevation="0"
      class="d-flex flex-column flex-wrap justify-space-between"
    >
      <div
        v-if="!hasSetPassword"
        class="d-flex flex-column flex-md-row w-100 py-4 justify-space-between"
      >
        <VCol cols="12" md="6" class="d-flex flex-column">
          <div class="text-primary font-weight-bold text-start text-uppercase">
            {{ translate('checkout:advantages') }}
          </div>
          <div class="d-flex flex-row mt-5">
            <VIcon color="success">custom:checkCircle</VIcon>
            <span class="ml-2">{{ translate('checkout:advantages_1') }}</span>
          </div>
          <div class="d-flex flex-row mt-2">
            <VIcon color="success">custom:checkCircle</VIcon>
            <span class="ml-2">{{ translate('checkout:advantages_2') }}</span>
          </div>
          <div class="d-flex flex-row mt-2">
            <VIcon color="success">custom:checkCircle</VIcon>
            <span class="ml-2">{{ translate('checkout:advantages_3') }}</span>
          </div>
        </VCol>

        <VCol cols="12" md="6">
          <VSheet elevation="0" class="d-flex flex-column pa-3 mt-5 mt-md-0">
            <VTextField
              ref="pwRef"
              :aria-label="translate('login_pw_headline')"
              :label="translate('login_pw_headline')"
              outlined
              required
              v-model="password"
              :rules="[rules.required, rules.minLength]"
              :append-inner-icon="
                showPassword ? 'custom:eyeOn' : 'custom:eyeOff'
              "
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              @click:append-inner="showPassword = !showPassword"
              @keydown.enter="$refs.pw.focus()"
            />
            <VBtn
              class="mt-3"
              block
              :disabled="!pwRef?.isValid || isPending"
              :loading="isPending"
              @click="setPassword"
            >
              {{ translate('checkout.save') }}
            </VBtn>
          </VSheet>
        </VCol>
      </div>
      <div v-else class="d-flex justify-center align-center pa-3">
        <VIcon color="success">custom:checkCircle</VIcon>
        <span class="ml-2">{{
          translate('checkout:password_set_successfully')
        }}</span>
      </div>
    </VSheet>
  </div>
</template>
